import { useState } from 'react';
import PublicNav from '../components/PublicNav';
import PageMeta from '../components/PageMeta';
import Footer from '../components/Footer';
import { CRYPTO_USD_PAIRS, CRYPTO_CROSS_PAIRS } from '../lib/cryptoPairs';

const FAQS = [
  {
    group: 'Getting Started',
    items: [
      {
        q: 'How do I connect my bank account?',
        a: 'Go to Account → Bank Accounts → Connect a Bank. Agence uses Plaid to securely link your account — you\'ll log in to your bank directly through Plaid\'s interface and Agence never sees your banking credentials.',
      },
      {
        q: 'How do I connect my investment account?',
        a: 'Go to Account → Trading Accounts → Connect your brokerage account, then authorize Agence on your brokerage\'s site. Agence uses your paper trading environment by default — no real money is involved until live trading is enabled.',
      },
      {
        q: 'What is paper trading?',
        a: 'Paper trading is a simulated trading environment provided by your brokerage. Orders are fictional — no real securities are purchased and no real money moves. It\'s the default mode for all automations in Agence and is safe to experiment with.',
      },
      {
        q: 'How do I sell or buy several positions at once?',
        a: 'On Portfolio, tap Bulk trade, then tick the positions you want (or Select all). Choose Sell to sell all of each position or a percentage of each, or Buy to add the same number of shares or dollars to each one you already hold. A review shows every order before anything is placed: positions you sell in full have their open orders cancelled first, any automations on that account must be paused to continue, and you type the account name when selling everything. Bulk trade works on paper accounts; it isn\'t available on live accounts yet.',
      },
      {
        q: 'What is a household account?',
        a: 'A household account lets you share a combined financial dashboard with a partner. Each person keeps their own connected accounts and credentials private; the household view shows combined balances, goals, and insights. Set it up in Account → Household.',
      },
    ],
  },
  {
    group: 'Insights & AI',
    items: [
      {
        q: 'How are insights generated?',
        a: 'Agence runs 12 specialized AI agents in parallel across your spending, portfolio, goals, anomalies, and market data. An LLM-as-judge synthesizes all agent outputs into a single ranked feed. Insights are refreshed on demand — click the refresh button on the Insights page.',
      },
      {
        q: 'Why is my insight feed empty?',
        a: 'Insights require at least one connected account. If you have a bank account connected via Plaid or a connected brokerage account, try refreshing. If the feed still shows nothing, check that your accounts are connected successfully in Settings.',
      },
      {
        q: 'How do I dismiss or snooze an insight?',
        a: 'Each insight card has dismiss and snooze controls. Dismissing removes it permanently from your feed. Snoozing hides it temporarily — it will reappear after the snooze period if the underlying condition persists.',
      },
    ],
  },
  {
    group: 'What can I ask Agence AI?',
    items: [
      {
        q: 'What kinds of questions can I ask the chat?',
        a: 'Anything about your financial data. Examples: "How much did I spend on food last month?", "What\'s my portfolio worth?", "Am I on track for my vacation goal?", "What\'s the news on NVDA?", "Show me my last 10 trades.", "How much did I earn this year?", "Do I have any budget alerts?". The AI pulls live data from your connected accounts to answer accurately.',
      },
      {
        q: 'Can the AI take actions on my behalf?',
        a: 'Yes — the AI can create, update, or delete savings goals; set or remove monthly spending budgets; and create, configure, enable, disable, or delete automations. Just describe what you want: "Set my food budget to $400", "Create a goal called House Down Payment for $50,000", "Disable my swing trade automation", "Set stop-loss to 5% on my main automation."',
      },
      {
        q: 'What requires manual action in the app?',
        a: 'A few things must be done manually: linking or unlinking bank and brokerage accounts, two-factor authentication settings, household member management, and placing individual trades. These are intentionally kept manual — account linking involves OAuth flows that require browser interaction, and individual trade execution involves financial authorization that requires explicit user action.',
      },
      {
        q: 'Why can\'t the AI place trades directly?',
        a: 'Trade execution involves real (or simulated) financial orders and carries meaningful risk. Requiring explicit action in the Portfolio page ensures every order is authorized by you directly rather than triggered conversationally. This is consistent with how regulated brokerages operate — automation rules you configure in advance are different from ad-hoc conversational orders.',
      },
    ],
  },
  {
    group: 'Automations (Autopilot)',
    items: [
      {
        q: 'What is an autopilot automation?',
        a: 'An automation is a rule-based strategy that monitors market signals according to your configured parameters, on either a paper or live account. It runs on a schedule, evaluates a composite signal score across up to 7 inputs, and flags a buy or sell signal when the score exceeds your risk threshold — on standard plans, you confirm each one before it\'s placed.',
      },
      {
        q: 'Why didn\'t my automation fire?',
        a: 'Several conditions can prevent a trade: (1) the composite signal score didn\'t meet your risk profile threshold; (2) the automation is outside configured market hours; (3) the ticker is in a per-ticker cooldown window after a recent trade; (4) the daily execution cap was reached; (5) an earnings announcement within 48 hours blocked a buy signal. Check the automation\'s run log for details.',
      },
      {
        q: 'What signals does the automation use?',
        a: 'The composite score is built from up to 7 inputs: 24h price change, news sentiment, unrealized P&L %, portfolio concentration, market regime (SPY/VIXY), analyst consensus, and insider MSPR. Signal weights vary by trading style (Day Trade, Swing, Long-term). See the Automation Risk Disclosure for the full breakdown.',
      },
      {
        q: 'What is the difference between paper and live trading?',
        a: 'Paper trading is fully simulated — no real money moves. Live trading places orders through a real brokerage account and real money is at risk. Automations can run on either — on both, you review and confirm each trade before it\'s placed. Before connecting a live account, read the Automation Risk Disclosure.',
      },
    ],
  },
  {
    group: 'Fees & Trading Costs',
    items: [
      {
        q: 'Does Agence charge trading fees?',
        a: 'Agence does not charge commissions or trading fees. Crypto trades carry a 0.25% fee per side (0.50% round-trip), but US equity (stock/ETF) trading is commission-free.',
      },
      {
        q: 'What are the crypto trading fees?',
        a: 'Crypto trading carries a taker fee of 0.25% per side — a full buy-then-sell round trip costs 0.50% in total fees. These fees are not charged by Agence; they apply to both paper and live accounts. Fee rates decrease as your 30-day trading volume grows.',
      },
      {
        q: 'Does the autopilot account for crypto fees?',
        a: 'Yes. Agence automatically factors in the 0.50% crypto round-trip brokerage fee when scoring crypto signals: a position with less than 0.50% gross gain is treated as net-neutral or net-negative in the signal score, so the autopilot won\'t exit a trade that would be wiped out by fees. Stop-loss and take-profit triggers are also adjusted: a stop-loss set at 2% actually fires when the gross P&L hits –1.75% (saving the 0.25% exit fee for the limit), and a take-profit set at 5% fires at 5.25% gross (so you actually net the full 5%). This means paper trading runs are valid test beds for live fee-inclusive strategies.',
      },
      {
        q: 'Are there fees on equity (stock/ETF) trades?',
        a: 'No. US equity and ETF trading is commission-free — no maker/taker fees, no per-share fees, no per-trade charges. Regulatory fees (SEC and FINRA) may apply on sell orders; these are fractions of a cent per share collected on behalf of regulators.',
      },
    ],
  },
  {
    group: 'Crypto Trading',
    items: [
      {
        q: 'Which cryptocurrencies can I trade?',
        a: 'CRYPTO_PAIRS_TABLE',
      },
      {
        q: 'What does the /USD suffix mean in crypto tickers?',
        a: 'It\'s standard exchange notation for a trading pair. BTC/USD means "Bitcoin traded against US dollars." All supported crypto assets on the platform use USD as the quote currency — you buy crypto by spending USD and sell it back for USD. You don\'t need USD in any special wallet; your connected brokerage account handles it automatically.',
      },
      {
        q: 'Why can\'t I trade a crypto I found in the search bar?',
        a: 'Assets in the search results are all known pairs, but tradability can be temporarily restricted for regulatory, liquidity, or maintenance reasons. If the autopilot tries to trade a suspended pair and fails three times in a row, it auto-blocks the ticker so it stops wasting attempts. You\'ll see the block in the Blocked Tickers section of your automation. Remove it once the pair is available again.',
      },
    ],
  },
  {
    group: 'Extended Hours Trading',
    items: [
      {
        q: 'What is extended hours trading?',
        a: 'Extended hours trading lets your automations place orders outside regular market hours (9:30 AM–4:00 PM ET). Agence supports two extended sessions: pre-market (4:00–9:30 AM ET) and after-hours (4:00–8:00 PM ET), Monday through Friday. Extended hours trading is off by default and must be enabled per automation. To enable: open an automation → uncheck "Market hours only" → expand Extended Hours → check "Enable extended hours trading".',
      },
      {
        q: 'Why are only limit orders allowed in extended hours?',
        a: 'Only limit orders are supported during extended hours — market orders are not available in pre-market or after-hours sessions. Agence enforces this automatically: when extended hours is on and your automation fires in an extended session, the order type is forced to limit regardless of your Order Type setting. The limit price is set at the last known price plus your Extended Hours Slippage Buffer (default 0.5%, higher than the regular 0.1% because extended hours spreads are much wider).',
      },
      {
        q: 'Why can\'t I use a dollar trade size in extended hours?',
        a: 'Dollar-notional orders and fractional shares are not supported during extended hours sessions. When your automation fires in an extended window, Agence converts your dollar trade size to whole shares automatically: shares = floor(trade size / price). For example, $50 at $182/share = 0 shares, so no order is placed — make sure your trade size is large enough relative to the stock price.',
      },
      {
        q: 'Why are stop-loss and take-profit disabled in extended hours?',
        a: 'Bracket orders require stop-market exit legs, which are not supported in extended hours sessions. When extended hours trading is on and both stop-loss and take-profit are configured, Agence skips the bracket legs and places a plain limit entry order instead. A note is recorded in the automation\'s execution log.',
      },
      {
        q: 'When do extended hours orders expire?',
        a: 'All extended hours orders use time_in_force = day, meaning they auto-cancel at the end of the session. A pre-market order not filled by 9:30 AM ET is cancelled. An after-hours order not filled by 8:00 PM ET is cancelled. Unfilled orders do not roll over to the next session or into regular market hours.',
      },
    ],
  },
  {
    group: 'Account & Data',
    items: [
      {
        q: 'What data does Agence access?',
        a: 'Bank transactions and balances via Plaid (read-only). Portfolio positions and trades via your connected brokerage (paper or live). News sentiment via Finnhub. Agence does not sell your data to third parties. See the Privacy Policy for full details.',
      },
      {
        q: 'How do I delete my account?',
        a: 'Go to Account → scroll to the bottom → Delete Account. This is permanent and cannot be undone. All your data, connected accounts, automations, and goals will be removed.',
      },
      {
        q: 'Can I use Agence for a shared household?',
        a: 'Yes. Account → Household → Invite Partner. Both users keep their own accounts private; the household dashboard shows a combined view. Shared goals can be created and tracked together.',
      },
    ],
  },
  {
    group: 'Troubleshooting',
    items: [
      {
        q: 'My bank data isn\'t updating.',
        a: 'Your Plaid connection may need re-authentication (banks periodically require this). Go to Account → Bank Accounts and look for a "Reconnect" prompt next to any account showing an error. If no error is shown, try disconnecting and reconnecting the account.',
      },
      {
        q: 'My autopilot can\'t buy anything.',
        a: 'Usually the account has negative buying power: its positions use up the cash, which often happens after duplicating an autopilot, since the copy trades the same account. To start fresh, sell the old positions with Bulk trade on Portfolio (select them, or Select all, then Sell). Bulk trade works on paper accounts; it isn\'t available on live accounts yet.',
      },
      {
        q: 'My portfolio shows incorrect or stale values.',
        a: 'Portfolio data is fetched live from your brokerage on page load. Try refreshing the page. If values are still wrong, check that your brokerage connection is valid in Account → Trading Accounts. Paper accounts may occasionally lag — this is an upstream issue.',
      },
      {
        q: 'The insight feed is stuck loading.',
        a: 'Refresh the page. If it persists, verify that at least one account (bank or investment) is connected in Settings. If all accounts are connected and the issue continues, use the chat to report it or email support.',
      },
      {
        q: 'I got logged out unexpectedly.',
        a: 'On the web, Agence logs you out after 15 minutes of inactivity for security — you\'ll see a warning with a countdown first, with the option to stay logged in. Beyond that, a session that\'s gone completely unused (browser fully closed, no activity at all) expires after 30 days. Log back in with your email and password. If you\'re being logged out more frequently than that, try clearing your browser\'s local storage or checking for browser extensions that block storage. On the mobile app, look for Face ID/Touch ID in Settings instead — that\'s a separate, on-device unlock convenience layered on top of your regular login.',
      },
    ],
  },
];

function CryptoPairsTable() {
  return (
    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.7, marginRight: '1.5rem', marginBottom: '0.85rem' }}>
      <p style={{ margin: '0 0 0.5rem' }}>
        <strong style={{ color: 'var(--text-primary)' }}>USD pairs</strong> — {CRYPTO_USD_PAIRS.length} assets traded against US dollars:
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '0.2rem 1.5rem', marginBottom: '1rem' }}>
        {CRYPTO_USD_PAIRS.map(({ symbol, name }) => (
          <span key={symbol} style={{ display: 'flex', gap: '0.4rem', alignItems: 'baseline' }}>
            <code style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)', background: 'var(--navy-100)', borderRadius: '3px', padding: '0 0.3rem' }}>{symbol}</code>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{name}</span>
          </span>
        ))}
      </div>
      <p style={{ margin: '0 0 0.5rem' }}>
        <strong style={{ color: 'var(--text-primary)' }}>Cross-pairs</strong> — crypto-to-crypto and stablecoin pairs:
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '0.2rem 1.5rem', marginBottom: '0.75rem' }}>
        {CRYPTO_CROSS_PAIRS.map(({ symbol, name }) => (
          <span key={symbol} style={{ display: 'flex', gap: '0.4rem', alignItems: 'baseline' }}>
            <code style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)', background: 'var(--navy-100)', borderRadius: '3px', padding: '0 0.3rem' }}>{symbol}</code>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{name}</span>
          </span>
        ))}
      </div>
      <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
        Availability may vary — pairs can be temporarily suspended by your brokerage for regulatory or liquidity reasons. Cross-pairs are for manual trading; the autopilot only supports USD-denominated pairs.
      </p>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);

  let body = null;
  if (open) {
    if (a === 'CRYPTO_PAIRS_TABLE') {
      body = <CryptoPairsTable />;
    } else {
      body = <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 1.5rem 0.85rem 0' }}>{a}</p>;
    }
  }

  return (
    <div style={{ borderBottom: '1px solid var(--navy-200)' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 0',
          textAlign: 'left',
          gap: '1rem',
        }}
      >
        <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.4 }}>{q}</span>
        <span
          aria-hidden="true"
          style={{
            fontSize: '0.8rem',
            color: 'var(--text-muted)',
            flexShrink: 0,
            display: 'inline-block',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 180ms ease',
          }}
        >
          ▾
        </span>
      </button>
      {body}
    </div>
  );
}

export default function Help() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', flexDirection: 'column' }}>
      <PageMeta title="Help" description="Help center for Agence — connect bank accounts, understand AI budget and investment insights, and manage trading automations." path="/help" />
      <PublicNav />
      <main style={{ flex: 1, maxWidth: 720, margin: '0 auto', padding: '2rem 1.5rem 4rem', width: '100%' }}>

        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          Help & Support
        </h1>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '2rem' }}>
          Answers to common questions — or reach us directly below.
        </p>

        {/* Chat callout */}
        <div style={{
          background: 'var(--navy-100)',
          border: '1px solid var(--navy-300)',
          borderRadius: 'var(--radius-md)',
          padding: '1rem 1.25rem',
          marginBottom: '2.5rem',
          display: 'flex',
          gap: '1rem',
          alignItems: 'flex-start',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--navy-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <div>
            <p style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.3rem' }}>
              Need help right now?
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.65, margin: 0 }}>
              Use the <strong>chat</strong> (bottom-right corner) for immediate assistance — it can troubleshoot issues, explain any feature, walk you through automations, or answer questions about your finances. Available on every page once you&apos;re logged in.
            </p>
          </div>
        </div>

        {/* FAQ sections */}
        {FAQS.map(({ group, items }) => (
          <div key={group} style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.25rem', borderBottom: '2px solid var(--navy-300)', paddingBottom: '0.4rem' }}>
              {group}
            </h2>
            <div>
              {items.map(({ q, a }) => (
                <FaqItem key={q} q={q} a={a} />
              ))}
            </div>
          </div>
        ))}

        {/* Human support */}
        <div style={{
          borderTop: '1px solid var(--navy-200)',
          paddingTop: '1.5rem',
          marginTop: '0.5rem',
        }}>
          <p style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>
            Still need help?
          </p>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0 }}>
            Email us at{' '}
            <a href="mailto:support@agence.finance" style={{ color: 'var(--navy-600)', textDecoration: 'underline' }}>
              support@agence.finance
            </a>
            . We typically respond within 24–48 hours. For faster help, use the chat feature described above.
          </p>
        </div>

      </main>
      <Footer />
    </div>
  );
}
